import dayjs from 'dayjs'
import decode from 'jwt-decode'
import {
    listProcedures,
    getLocations,
    listPatients,
    postAppointment,
    listAvailableTimes,
    getCustomization,
    getChairfillData,
    listProcedureProviders,
    listLocationInsuranceProviders,
    getLocationInsuranceSettings,
    postAuthenticateUser,
    getLocationReschedulingConfiguration,
    listAvailableReschedulingTimes,
    postCancelAppointment,
    postRescheduleAppointment,
    getLocationById,
} from '../api'
import { Patient } from '../api/interfaces'
import {
    AppointmentManagementReschedulingStatus,
    AppointmentManagementStep,
    AppointmentManagementUserActions,
    BookingStep,
    ReschedulingConfigurationType,
} from '../models/enums'
import { PatientDetails } from '../models/patient'
import { Location, InsuranceSetting, InsuranceSettings, InsuranceProvider } from '../models/location'
import isoDate, { apiDate } from '../shared/isoDate'
import { DATE_FORMAT, CALENDAR_DATE_FORMAT } from '../shared/const'
import { SaveAppointment } from '../models/saveAppointment'
import { customErrorMessages } from '../shared/customErrorMessages'
import { Actions } from './reducer'
import URL_TOKEN from '../shared/urlToken'

type Dispatch = (actions: Actions) => void

export async function fetchProcedures(dispatch: Dispatch, locationId: string) {
    try {
        const { data: procedures = [] } = await listProcedures(locationId)

        dispatch({
            type: 'PROCEDURES_RECEIVED',
            procedures,
        })

        return procedures
    } catch (e) {
        dispatch({
            type: 'HANDLE_ERROR',
            errorMessage: 'Unable to fetch procedures',
        })
    }
}

export async function fetchCustomization(dispatch: Dispatch) {
    try {
        const { data: customization } = await getCustomization()

        dispatch({
            type: 'RECEIVE_CUSTOMIZATION',
            customization,
        })
        return customization
    } catch (e) {
        dispatch({
            type: 'CUSTOMIZATION_ERROR',
        })

        throw new Error(e as any)
    }
}

export async function fetchLocations(dispatch: Dispatch) {
    try {
        await dispatch({
            type: 'SET_LOADER',
            spin: true,
        })

        const { data: locations } = await getLocations()

        if (locations.length === 1 && locations[0].onlineSchedulingEnabled) {
            await dispatch({
                type: 'SET_SELECTED_LOCATION',
                locationId: locations[0].id,
            })
        }

        await dispatch({
            type: 'RECEIVE_LOCATIONS',
            locations,
        })

        dispatch({
            type: 'SET_LOADER',
            spin: false,
        })

        return locations
    } catch (e) {
        dispatch({
            type: 'SET_LOADER',
            spin: false,
        })

        throw new Error(e as any)
    }
}

export async function fetchLocation(dispatch: Dispatch, locationId: string) {
    try {
        await dispatch({
            type: 'SET_LOADER',
            spin: true,
        })

        const { data: location } = await getLocationById(locationId)

        if (location.onlineSchedulingEnabled) {
            await dispatch({
                type: 'SET_SELECTED_LOCATION',
                locationId: location.id,
            })
        }

        await dispatch({
            type: 'RECEIVE_LOCATIONS',
            locations: [{ ...location, ...location.address, id: location.id }],
        })

        dispatch({
            type: 'SET_LOADER',
            spin: false,
        })

        return location
    } catch (e) {
        dispatch({
            type: 'SET_LOADER',
            spin: false,
        })

        throw new Error(e as any)
    }
}

export async function changeLocation(dispatch: Dispatch, location: Location) {
    if (!location.onlineSchedulingEnabled) {
        return
    }

    dispatch({
        type: 'SET_SELECTED_LOCATION',
        locationId: location.id,
    })

    dispatch({
        type: 'RESET_APPOINTMENT_DATETIME',
    })

    dispatch({
        type: 'PROCEDURES_RECEIVED',
        procedures: [],
    })

    dispatch({
        type: 'RESET_PATIENT_DETAILS',
        fieldName: 'reasonOfAppointment',
    })

    await fetchProcedures(dispatch, location.id)
}

const guessPatient = (patients: Patient[], patientDetails: PatientDetails) => {
    const guessedByName = patients.filter(
        (patient) =>
            patient.first_name.toLowerCase().trim() === patientDetails.firstName.value?.toLowerCase().trim() &&
            patient.last_name.toLowerCase().trim() === patientDetails.lastName.value?.toLowerCase().trim() &&
            patient.birth_date === apiDate(patientDetails.dateOfBirth.value)
    )

    if (guessedByName.length === 1) {
        return guessedByName[0]
    }

    const filteredPatients = guessedByName.length > 1 ? guessedByName : patients
    const guessedByEmail = filteredPatients.filter(
        (patient) =>
            patient.email_address.toLowerCase().trim() === patientDetails.emailAddress.value?.toLowerCase().trim()
    )

    if (guessedByEmail.length === 1) {
        return guessedByEmail[0]
    }

    const guessedByPhone = (guessedByEmail.length > 1 ? guessedByEmail : filteredPatients).filter(
        (patient) => patient.mobile_phone === patientDetails.phone.value
    )

    if (guessedByPhone.length === 1) {
        return guessedByPhone[0]
    }

    // TODO: implement last visit

    return patients[0]
}

export async function fetchPatient(
    dispatch: Dispatch,
    {
        patientDetails,
        locationId,
        contactPhone,
    }: { patientDetails: PatientDetails; locationId: string; contactPhone: string }
) {
    await dispatch({
        type: 'HANDLE_SUBMITTING',
    })

    try {
        const { data: patients } = await listPatients({
            first_name: patientDetails.firstName.value || '',
            last_name: patientDetails.lastName.value || '',
            birth_date: patientDetails.dateOfBirth.value
                ? isoDate(dayjs(patientDetails.dateOfBirth.value, DATE_FORMAT).toDate())
                : '',
            location_id: locationId,
        })

        if (!Array.isArray(patients) || patients.length === 0) {
            const errorMessage = `We are having trouble finding you! You can book your appointment as a new patient${
                contactPhone ? `,` : ` or`
            } chat with us live on our website${contactPhone ? `, or call us at ${contactPhone}` : ``}.`

            dispatch({
                type: 'PATIENT_SEARCH_NO_RESULTS',
                errorMessage,
            })
            return false
        }

        const patient = patients.length === 1 ? patients[0] : guessPatient(patients, patientDetails)

        if (patient.hasAppointmentsInLast7Days) {
            dispatch({
                type: 'PATIENT_HAS_APPOINTMENTS_IN_LAST_7_DAYS',
                patientId: patient.id,
            })
            return false
        }

        dispatch({
            type: 'PATIENT_RECEIVED',
            patientId: patient.id,
        })
        return true
    } catch (e) {
        dispatch({
            type: 'HANDLE_ERROR',
            errorMessage: 'Unable to fetch patients',
        })
        return false
    }
}

export async function cancelPatientHasAppointmentsInLast7DaysModal(dispatch: Dispatch) {
    dispatch({
        type: 'PATIENT_HAS_APPOINTMENTS_IN_LAST_7_DAYS_CANCEL',
    })
}

export async function proceedPatientHasAppointmentsInLast7DaysModal(dispatch: Dispatch, patientId: string) {
    dispatch({
        type: 'PATIENT_HAS_APPOINTMENTS_IN_LAST_7_DAYS_PROCEED',
        patientId: patientId,
    })
}

export async function fetchAvailableTimes(
    dispatch: Dispatch,
    {
        locationId,
        procedureId,
        providerId,
        from,
        to,
        durationInSeconds,
    }: {
        locationId: string
        procedureId: string
        providerId?: string
        from: string
        to: string
        durationInSeconds?: number
    }
) {
    try {
        await dispatch({
            type: 'SET_LOADER',
            spin: true,
        })
        const { data: availability } = await listAvailableTimes(locationId, procedureId, {
            from,
            to,
            durationInSeconds,
            providerId,
        })

        await dispatch({
            type: 'RECEIVE_AVAILABLE_TIMES',
            availability,
        })
        dispatch({
            type: 'SET_LOADER',
            spin: false,
        })

        return availability
    } catch (e) {
        await dispatch({
            type: 'HANDLE_ERROR',
            errorMessage: 'Unable to fetch available times',
        })
    }
}

export async function bookAppointment(
    dispatch: Dispatch,
    { locationId, procedureId, payload }: { locationId: string; procedureId: string; payload: SaveAppointment }
) {
    try {
        const response = await postAppointment(locationId, procedureId, payload)

        if (!response || response.statusCode !== 200) {
            dispatch({
                type: 'HANDLE_ERROR',
                errorMessage: response?.message ?? 'Cannot book an appointment',
                statusCode: response?.statusCode || '',
            })
            return ''
        }

        window.dataLayer?.push({ event: 'submitSelfScheduler' })
        window.dataLayer?.push({ event: 'simplifeyeSchedulingSubmit' })

        dispatch({
            type: 'CHANGE_STEP',
            bookingStep: BookingStep.RESERVING,
        })

        return response.data.id || ''
    } catch (e: any) {
        const pmsMessages = ['No available time slots on specified date', 'Specified time slot is not available']
        const message = e.message ?? 'Cannot book an appointment'
        dispatch({
            type: 'HANDLE_ERROR',
            errorMessage: customErrorMessages(message),
            statusCode: pmsMessages.indexOf(message) > -1 ? 422 : '',
        })

        return ''
    }
}

export async function changeStep(dispatch: Dispatch, bookingStep: BookingStep) {
    await dispatch({
        type: 'CHANGE_STEP',
        bookingStep,
    })
}

export async function loadChairfillData(dispatch: Dispatch, patientId: string, chairfillId: string) {
    try {
        const data = await getChairfillData(patientId, chairfillId)

        if (data.isAppointmentScheduled) {
            dispatch({
                type: 'LOAD_EXPIRED_RSVP_DATA',
                data,
            })
        } else {
            dispatch({ type: 'PATIENT_RECEIVED', patientId })
            dispatch({
                type: 'LOAD_RSVP_DATA',
                data,
            })
        }
    } catch (e) {
        dispatch({
            type: 'HANDLE_ERROR',
            errorMessage: (e as any).message ?? 'Cannot load RSVP data.',
        })
    }
}

export async function applyDistanceMatrix(dispatch: Dispatch, locations: Location[]) {
    dispatch({
        type: 'SET_DISTANCE_MATRIX',
        locations,
    })
}

export async function fetchProcedureProviders(dispatch: Dispatch, locationId: string, procedureId: string) {
    try {
        const { data: providers = [] } = await listProcedureProviders(locationId, procedureId)

        dispatch({
            type: 'PROVIDERS_RECEIVED',
            providers,
        })

        return providers
    } catch (e) {
        dispatch({
            type: 'HANDLE_ERROR',
            errorMessage: 'Unable to fetch procedure providers',
        })
    }
}

export async function loadInsuranceSettings(dispatch: Dispatch, locationId: string) {
    try {
        const { data } = await getLocationInsuranceSettings(locationId)
        const insuranceSettings = data.reduce(
            (settings: InsuranceSettings, record: InsuranceSetting) => ({
                ...settings,
                [record.key.replace('scheduling_insurance_provider_', '')]: record.value,
            }),
            {}
        )

        dispatch({
            type: 'INSURANCE_SETTINGS_RECEIVED',
            insuranceSettings,
        })

        if (insuranceSettings.show) {
            fetchInsuranceProviders(dispatch, locationId)
        }

        return insuranceSettings
    } catch (e) {
        dispatch({
            type: 'HANDLE_ERROR',
            errorMessage: 'Unable to fetch insurance settings',
        })
    }
}

export async function fetchInsuranceProviders(dispatch: Dispatch, locationId: string) {
    try {
        const { data: providers } = await listLocationInsuranceProviders(locationId)

        dispatch({
            type: 'INSURANCE_PROVIDERS_RECEIVED',
            insuranceProviders: providers as InsuranceProvider[],
        })

        return providers
    } catch (e) {
        dispatch({
            type: 'HANDLE_ERROR',
            errorMessage: 'Unable to fetch insurance providers',
        })
    }
}

export async function setStartingURL(dispatch: Dispatch, startingURL: string) {
    dispatch({
        type: 'SET_STARTING_URL',
        startingURL,
    })
}

export async function changeApptManagementStep(dispatch: Dispatch, apptManagementStep: AppointmentManagementStep) {
    await dispatch({
        type: 'CHANGE_APPT_MANAGEMENT_STEP',
        apptManagementStep,
    })
}

export async function changeApptManagementUserAction(
    dispatch: Dispatch,
    selectedApptUserAction: AppointmentManagementUserActions
) {
    await dispatch({
        type: 'CHANGE_APPT_MANAGEMENT_USER_ACTION',
        selectedApptUserAction,
    })
}

export async function updateApptManagementAuthKey(dispatch: Dispatch, authKey?: string) {
    await dispatch({
        type: 'UPDATE_AUTH_KEY',
        authKey,
    })
}

export async function updateManagedAppointmentDate(dispatch: Dispatch, managedAppointmentDate: string) {
    await dispatch({
        type: 'UPDATE_USER_MANAGED_APPT_DATE',
        managedAppointmentDate,
    })
}

export async function authenticateUser(
    dispatch: Dispatch,
    locationId: string,
    firstName: string,
    lastName: string,
    apptDate: string
) {
    try {
        dispatch({
            type: 'HANDLE_ERROR',
            errorMessage: '',
        })

        const {
            data: { jwt_token },
        } = await postAuthenticateUser(locationId, {
            jwt_token: URL_TOKEN || '',
            first_name: firstName,
            last_name: lastName,
            appointment_date: dayjs(apptDate, DATE_FORMAT).format(CALENDAR_DATE_FORMAT),
        })

        updateApptManagementAuthKey(dispatch, jwt_token)

        const { appointment_date }: { appointment_date: string } = decode(jwt_token)

        updateManagedAppointmentDate(dispatch, appointment_date)

        changeApptManagementStep(dispatch, AppointmentManagementStep.ACTION_TYPE_SELECTION)
    } catch (e) {
        dispatch({
            type: 'HANDLE_ERROR',
            errorMessage: 'Unable to authenticate user!',
        })
    }
}

export async function fetchLocationReschedulingConfiguration(dispatch: Dispatch, locationId: string, token: string) {
    try {
        const { data } = await getLocationReschedulingConfiguration(locationId, token)
        dispatch({
            type: 'UPDATE_LOCATION_RESCHEDULING_CONFIGURATION',
            reschedulingConfiguration: data,
        })
    } catch (e) {
        dispatch({
            type: 'HANDLE_ERROR',
            errorMessage: 'Unable to fetch configuration!',
        })
        dispatch({
            type: 'UPDATE_LOCATION_RESCHEDULING_CONFIGURATION',
            reschedulingConfiguration: {
                cancel_appointment: ReschedulingConfigurationType.DISABLE,
                reschedule_appointment: ReschedulingConfigurationType.DISABLE,
            },
        })
    }
}

export async function fetchAvailableReschedulingTimes(
    dispatch: Dispatch,
    locationId: string,
    from: string,
    to: string,
    token: string
) {
    try {
        await dispatch({
            type: 'SET_LOADER',
            spin: true,
        })

        const { data: availability } = await listAvailableReschedulingTimes(locationId, from, to, token)

        await dispatch({
            type: 'RECEIVE_AVAILABLE_TIMES',
            availability: availability,
        })

        dispatch({
            type: 'SET_LOADER',
            spin: false,
        })

        return availability
    } catch (e) {
        dispatch({
            type: 'HANDLE_ERROR',
            errorMessage: 'Unable to fetch available times',
        })
    }
}

export async function cancelAppointment(dispatch: Dispatch, locationId: string, reason: string, token: string) {
    try {
        changeApptManagementStep(dispatch, AppointmentManagementStep.CANCEL_CONFIRM)

        await postCancelAppointment(locationId, reason, token)

        await changeApptManagementStep(dispatch, AppointmentManagementStep.CANCELED)
    } catch (e) {
        dispatch({
            type: 'HANDLE_ERROR',
            errorMessage: 'Unable to cancel your appointment',
        })
        changeApptManagementStep(dispatch, AppointmentManagementStep.CANCEL)
    }
}

export async function rescheduleAppointment(
    dispatch: Dispatch,
    locationId: string,
    transactionId: string,
    appointmentTime: string,
    token: string
) {
    try {
        dispatch({
            type: 'UPDATE_USER_MANAGED_APPT_COMPLETION',
            managedAppointmentRescheduleComplete: AppointmentManagementReschedulingStatus.RESCHEDULING,
        })
        const response = await postRescheduleAppointment(locationId, transactionId, appointmentTime, token)

        if (!response || response.statusCode !== 200) {
            dispatch({
                type: 'HANDLE_ERROR',
                errorMessage: response?.message ?? 'Cannot reschedule your appointment',
                statusCode: response?.statusCode || '',
            })

            return false
        }
        return true
    } catch (e: any) {
        const pmsMessages = ['No available time slots on specified date', 'Specified time slot is not available']
        const message = e.message ?? 'Cannot reschedule your appointment'
        dispatch({
            type: 'HANDLE_ERROR',
            errorMessage: customErrorMessages(message),
            statusCode: pmsMessages.indexOf(message) > -1 ? 422 : '',
        })
        return false
    }
}
